import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/navigation/PublicHeader';
import SearchBar from './components/SearchBar';
import RouteCard from './components/RouteCard';
import MapView from './components/MapView';
import EmptyState from './components/EmptyState';
import DisclaimerBanner from './components/DisclaimerBanner';
import FareInfoBox from './components/FareInfoBox';
import AIRecommendationBanner from './components/AIRecommendationBanner';
import { routeService } from '../../services/routeService';
import { scheduleService } from '../../services/scheduleService';
import { findNearestTrip } from '../../utils/etaCalculator';
import LocationPermissionPrompt from '../../components/modals/LocationPermissionPrompt';

// Helper function to calculate distance between two points (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
  Math.sin(dLat / 2) * Math.sin(dLat / 2) +
  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
  Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Helper function to find minimum distance from user to route polyline
const calculateDistanceToRoute = (userLocation, polyline) => {
  if (!userLocation || !polyline || polyline?.length === 0) return Infinity;

  let minDistance = Infinity;
  polyline?.forEach((point) => {
    const distance = calculateDistance(
      userLocation?.latitude,
      userLocation?.longitude,
      point?.lat,
      point?.lng
    );
    if (distance < minDistance) {
      minDistance = distance;
    }
  });

  return minDistance;
};

const Home = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [routeETAs, setRouteETAs] = useState({});
  const [calculatingETAs, setCalculatingETAs] = useState(false);

  useEffect(() => {
    loadRoutes();
    checkLocationPermission();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculate ETAs for all routes with AI enhancement
  useEffect(() => {
    const calculateAllETAs = async () => {
      if (!routes?.length || !userLocation) return;

      setCalculatingETAs(true);
      const etaPromises = routes?.map(async (route) => {
        try {
          // Validate route has required data
          if (!route?.id) {
            console.warn('Route missing ID, skipping ETA calculation');
            return { routeId: route?.id, trip: null };
          }

          const schedules = await scheduleService?.getByRouteId(route?.id);

          // Check if schedules exist
          if (!schedules || schedules?.length === 0) {
            return { routeId: route?.id, trip: null };
          }

          const polylineCoords = route?.geometry?.coordinates?.map((coord) => ({
            lat: coord?.[1],
            lng: coord?.[0]
          })) || [];

          if (polylineCoords?.length === 0) return { routeId: route?.id, trip: null };

          const trip = await findNearestTrip({
            userLocation,
            routePolyline: polylineCoords,
            departureTimes: schedules?.
            filter((s) => {
              const dayMask = s?.daysMask || [];
              const currentDay = currentTime?.getDay();
              const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
              return dayMask?.includes(dayNames?.[currentDay]);
            })?.
            flatMap((s) => {
              const times = [];
              const [startHour, startMinute] = s?.startTime?.split(':')?.map(Number);
              const [endHour, endMinute] = s?.endTime?.split(':')?.map(Number);
              const startMinutes = startHour * 60 + startMinute;
              const endMinutes = endHour * 60 + endMinute;

              for (let time = startMinutes; time <= endMinutes; time += s?.frequencyMinutes) {
                const hour = Math.floor(time / 60);
                const minute = time % 60;
                times?.push(`${String(hour)?.padStart(2, '0')}:${String(minute)?.padStart(2, '0')}`);
              }
              return times;
            }) || [],
            routeDurationMinutes: route?.durationMinutes || 25,
            currentTime,
            routeId: route?.id,
            useAI: true // Enable AI enhancement
          });

          return { routeId: route?.id, trip };
        } catch (error) {
          console.error(`Error calculating ETA for route ${route?.id}:`, {
            message: error?.message || 'Unknown error',
            details: error?.toString(),
            hint: error?.hint || '',
            code: error?.code || ''
          });
          // Return null trip instead of breaking the entire calculation
          return { routeId: route?.id, trip: null };
        }
      });

      try {
        const results = await Promise.all(etaPromises);
        const etaMap = results?.reduce((acc, { routeId, trip }) => {
          acc[routeId] = trip;
          return acc;
        }, {});

        setRouteETAs(etaMap);
      } catch (error) {
        console.error('Failed to calculate ETAs:', error);
        // Set empty ETA map on failure
        setRouteETAs({});
      } finally {
        setCalculatingETAs(false);
      }
    };

    calculateAllETAs();
  }, [routes, userLocation, currentTime]);

  // Calculate distances and update routes when user location changes
  useEffect(() => {
    if (!userLocation || !routes?.length) return;

    const routesWithDistance = routes?.map((route) => ({
      ...route,
      distanceToRoute: calculateDistanceToRoute(userLocation, route?.polyline)
    }));

    // Filter routes within 600 meters (0.6 km)
    const routesWithin300m = routesWithDistance?.filter((route) =>
    route?.distanceToRoute <= 0.6
    );

    // Sort by distance and take only 6 closest within 300m
    const sortedRoutes = [...routesWithin300m]?.sort((a, b) =>
    a?.distanceToRoute - b?.distanceToRoute
    );
    const closest6Routes = sortedRoutes?.slice(0, 6);

    // Pre-select the closest route (set visible to true)
    const routesWithSelection = closest6Routes?.map((route, index) => ({
      ...route,
      visible: true // All routes within 600m are visible
    }));

    // Update both routes and filteredRoutes to sync visibility state
    const updatedAllRoutes = routesWithDistance?.map((route) => {
      const matchingRoute = routesWithSelection?.find((r) => r?.id === route?.id);
      return matchingRoute ? matchingRoute : { ...route, visible: false };
    });

    setRoutes(updatedAllRoutes);
    setFilteredRoutes(routesWithSelection);
  }, [userLocation]);

  const checkLocationPermission = async () => {
    if (!navigator.geolocation) {
      setLoading(false);
      return;
    }

    try {
      const permission = await navigator.permissions?.query({ name: 'geolocation' });

      if (permission?.state === 'granted') {
        requestLocation();
      } else if (permission?.state === 'prompt') {
        setShowLocationPrompt(true);
        setLoading(false);
      } else {
        setShowLocationPrompt(true);
        setLoading(false);
      }
    } catch (error) {
      setShowLocationPrompt(true);
      setLoading(false);
    }
  };

  const requestLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position?.coords?.latitude,
          longitude: position?.coords?.longitude
        });
        setLoading(false);
        setShowLocationPrompt(false);
      },
      (error) => {
        console.error('Error getting location:', {
          code: error?.code,
          message: error?.message,
          PERMISSION_DENIED: error?.PERMISSION_DENIED,
          POSITION_UNAVAILABLE: error?.POSITION_UNAVAILABLE,
          TIMEOUT: error?.TIMEOUT
        });
        setLoading(false);
        setShowLocationPrompt(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleLocationPermission = async (granted) => {
    if (granted) {
      requestLocation();
    } else {
      setLoading(false);
    }
  };

  const loadRoutes = async () => {
    try {
      setLoading(true);
      const data = await routeService?.getActiveRoutes();

      // Load schedules for all routes
      const schedulePromises = data?.map((route) =>
      scheduleService?.getByRouteId(route?.id)?.catch(() => [])
      );
      const allSchedules = await Promise.all(schedulePromises);

      // Store schedules by route ID
      const scheduleMap = {};
      data?.forEach((route, index) => {
        scheduleMap[route?.id] = allSchedules?.[index] || [];
      });

      const formattedRoutes = data?.map((route) => {
        const polylineCoords = route?.geometry?.coordinates?.map((coord) => ({
          lat: coord?.[1],
          lng: coord?.[0]
        })) || [];

        return {
          id: route?.id,
          name: route?.routeName,
          startPoint: route?.startPoint,
          endPoint: route?.endPoint,
          distanceKm: route?.distanceKm || 0,
          estimatedMinutes: route?.durationMinutes || 0,
          nextDeparture: '--:--',
          confidenceMinutes: 3,
          active: route?.isActive,
          visible: false,
          geometry: route?.geometry || { type: 'LineString', coordinates: [] },
          polyline: polylineCoords,
          durationMinutes: route?.durationMinutes || 25
        };
      });
      setRoutes(formattedRoutes);
      setFilteredRoutes(formattedRoutes);
    } catch (err) {
      console.error('Failed to load routes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleRouteClick = (routeId) => {
    navigate(`/route-detail?id=${routeId}`);
  };

  const handleToggleRouteVisibility = (routeId) => {
    setRoutes((prevRoutes) =>
    prevRoutes?.map((route) =>
    route?.id === routeId ?
    { ...route, visible: !route?.visible } :
    route
    )
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader
        onSearch={() => {}}
        onSearchChange={() => {}} />

      
      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2 mt-[53px]">
            Yakınınızdaki Dolmuş Rotaları
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Konumunuza en yakın rotaları görüntüleyin ve varış sürelerini öğrenin
          </p>
          
          {/* TRAFİKKONYA Branding */}
          <div className="mt-4 flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex-1">
              <p className="text-sm md:text-base font-semibold text-blue-900 dark:text-blue-100">
                Bu proje <span className="text-lg font-bold text-blue-600 dark:text-blue-400">TRAFİK KONYA</span>'ya aittir
              </p>
            </div>
            <a
              href="https://www.instagram.com/trafikkonya/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-lg hover:scale-110 transition-transform duration-200 shadow-md"
              aria-label="TRAFİKKONYA Instagram">

              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg">

                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
          </div>
        </div>

        <div className="mb-6 md:mb-8">
          <FareInfoBox />
        </div>

        <div className="mb-6 md:mb-8">
          <MapView
            userLocation={userLocation}
            routes={routes}
            selectedRoute={null}
            onRouteSelect={handleToggleRouteVisibility} />

        </div>

        <div className="mb-6 md:mb-8">
          <SearchBar
            routes={routes}
            onSearch={handleSearch}
            onSearchChange={handleSearch}
            searchValue={searchQuery} />

        </div>

        {/* AI Recommendations Banner */}
        {userLocation &&
        <AIRecommendationBanner
          recommendations={[]}
          onRouteClick={handleRouteClick}
          isLoading={calculatingETAs} />

        }

        {loading ?
        <EmptyState
          onAction={() => {}} /> :

        filteredRoutes?.length > 0 ?
        <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2 space-y-4">
                {calculatingETAs &&
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                    <span className="text-sm text-primary">AI ile ETA hesaplanıyor...</span>
                  </div>
              }
                <div className="mb-3 text-sm text-muted-foreground">
                  600 metre içindeki en yakın rotalar gösteriliyor
                </div>
                {filteredRoutes?.map((route) => {
                const tripData = routeETAs?.[route?.id];
                // Format distance from user to route
                const distanceToRouteFormatted = route?.distanceToRoute ?
                route?.distanceToRoute < 1 ?
                `${Math.round(route?.distanceToRoute * 1000)} m` :
                `${route?.distanceToRoute?.toFixed(2)} km` :
                'Bilinmiyor';

                return (
                  <RouteCard
                    key={route?.id}
                    route={{
                      id: route?.id,
                      name: route?.name,
                      startPoint: route?.startPoint,
                      endPoint: route?.endPoint,
                      distance: distanceToRouteFormatted,
                      duration: `${route?.estimatedMinutes} dk`,
                      nextDeparture: tripData?.departureTime || 'Bilinmiyor',
                      estimatedMinutes: tripData?.etaMinutes || 0,
                      status: tripData?.status || 'unknown',
                      aiEnhanced: !!tripData?.aiPrediction
                    }}
                    onClick={() => handleRouteClick(route?.id)} />);


              })}
              </div>

              <div className="lg:col-span-1 space-y-6">
                <DisclaimerBanner />
              </div>
            </div>
          </> :

        <EmptyState
          onAction={() => {}} />

        }
      </main>

      {showLocationPrompt &&
      <LocationPermissionPrompt
        isOpen={showLocationPrompt}
        onAllow={() => handleLocationPermission(true)}
        onDeny={() => handleLocationPermission(false)}
        onRequestPermission={() => handleLocationPermission(true)}
        onManualEntry={() => handleLocationPermission(false)}
        onClose={() => setShowLocationPrompt(false)} />

      }
    </div>);

};

export default Home;